import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';

// ── CSV parser (state machine — gère les gros champs quotés avec sauts de ligne) ──
function* parseCSVRows(text: string): Generator<string[]> {
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { row.push(field); field = ''; }
      else if (ch === '\n') {
        row.push(field); field = '';
        yield row;
        row = [];
      } else if (ch !== '\r') { field += ch; }
    }
  }
  row.push(field);
  if (row.some(f => f !== '')) yield row;
}

function findCol(headers: string[], pattern: RegExp): number {
  return headers.findIndex(h => pattern.test(h.trim()));
}

// Normalise base64 : retire whitespace, préfixe data URI, corrige padding et url-safe chars
function normalizeBase64(s: string): string | null {
  if (!s.trim()) return null;
  let b = s.replace(/\s/g, '').replace(/^data:image\/[^;]+;base64,/, '');
  b = b.replace(/-/g, '+').replace(/_/g, '/');
  const pad = (4 - (b.length % 4)) % 4;
  return b ? b + (pad > 0 ? '='.repeat(pad) : '') : null;
}

// Détection du format image depuis les magic bytes
function detectExt(bytes: Uint8Array): string {
  const b = bytes;
  if (b.length >= 3 && b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'jpg';
  if (b.length >= 4 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return 'png';
  if (b.length >= 3 && b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) return 'gif';
  if (b.length >= 2 && b[0] === 0x42 && b[1] === 0x4D) return 'bmp';
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return 'webp';
  return 'jpg';
}

// ── Types ──
type ProcessingState = 'idle' | 'reading' | 'processing' | 'zipping' | 'done' | 'error';

interface Stats {
  total: number;
  ok: number;
  skipped: number;
  errors: number;
  progress: number;
  currentName: string;
  excelWarning: boolean;
}

const INITIAL_STATS: Stats = { total: 0, ok: 0, skipped: 0, errors: 0, progress: 0, currentName: '', excelWarning: false };

// ── Component ──
const OdooImages: React.FC = () => {
  const navigate = useNavigate();
  const [procState, setProcState] = useState<ProcessingState>('idle');
  const [stats, setStats] = useState<Stats>(INITIAL_STATS);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState('images.zip');
  const prevUrlRef = useRef<string | null>(null);

  const processFile = async (file: File) => {
    if (prevUrlRef.current) {
      URL.revokeObjectURL(prevUrlRef.current);
      prevUrlRef.current = null;
      setDownloadUrl(null);
    }

    setProcState('reading');
    setErrorMsg(null);
    setStats(INITIAL_STATS);

    let text: string;
    try {
      text = await file.text();
    } catch (e: any) {
      setProcState('error');
      setErrorMsg(`Impossible de lire le fichier : ${e.message}`);
      return;
    }

    setProcState('processing');

    const zip = new JSZip();
    const gen = parseCSVRows(text);

    // Lire l'en-tête
    const headerRow = gen.next();
    if (headerRow.done || !headerRow.value?.length) {
      setProcState('error');
      setErrorMsg('CSV vide ou illisible.');
      return;
    }
    const headers = headerRow.value;
    const idxId   = findCol(headers, /^id$/i);
    const idxCode = findCol(headers, /^default_code$|^référence interne$|^internal reference$/i);
    const idxImg  = findCol(headers, /^image_1920$|^image$/i);

    if (idxImg < 0) {
      setProcState('error');
      setErrorMsg("Colonne image introuvable. Vérifiez que votre export Odoo inclut le champ 'image_1920' (ou 'Image' en export standard).");
      return;
    }

    const fileSize = text.length;
    let charPos = headers.join(',').length + 1;
    let ok = 0, skipped = 0, errors = 0, total = 0;
    let excelTruncCount = 0;

    const yieldToMain = () => new Promise<void>(r => setTimeout(r, 0));

    // Itération manuelle via .next() — évite for...of sur Generator (incompatible es5 dans CRA)
    let next = gen.next();
    while (!next.done) {
      const row = next.value;
      next = gen.next();

      const imgRaw = row.length > idxImg ? row[idxImg] : '';
      charPos += row.join(',').length + 1;

      if (!imgRaw.trim()) { skipped++; total++; continue; }

      // Détection heuristique troncature Excel : limite cellule = 32 767 chars
      const rawLen = imgRaw.replace(/\s/g, '').length;
      if (rawLen > 0 && rawLen <= 32_767) excelTruncCount++;

      const id = idxId >= 0 ? row[idxId] ?? '' : '';
      const code = idxCode >= 0 ? row[idxCode] ?? '' : '';
      const name = (code.trim() || `product_${id}`).replace(/[\\/:*?"<>|]/g, '_');
      total++;

      setStats({ total, ok, skipped, errors, progress: Math.min(95, Math.round((charPos / fileSize) * 100)), currentName: name, excelWarning: false });

      try {
        const b64 = normalizeBase64(imgRaw);
        if (!b64) { skipped++; continue; }

        const binaryStr = atob(b64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);

        if (bytes.length < 100) { skipped++; continue; }

        const ext = detectExt(bytes);
        // STORE (pas de compression) : images déjà compressées, DEFLATE n'apporterait rien
        zip.file(`${name}.${ext}`, bytes, { compression: 'STORE' });
        ok++;
      } catch (e: any) {
        errors++;
        zip.file(`ERREUR_${name}.txt`, `Ligne ${total + 1}\nNom: ${name}\nErreur: ${e.message}`);
      }

      if (total % 5 === 0) await yieldToMain();
    }

    const excelWarning = ok > 0 && excelTruncCount === ok;
    setStats({ total, ok, skipped, errors, progress: 98, currentName: '', excelWarning });
    setProcState('zipping');

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    prevUrlRef.current = url;
    const zipName = `odoo-images-${new Date().toISOString().slice(0, 10)}.zip`;
    setDownloadUrl(url);
    setDownloadName(zipName);
    setStats(s => ({ ...s, progress: 100, currentName: '' }));
    setProcState('done');
  };

  const handleFile = (file: File | undefined) => {
    if (file) processFile(file);
  };

  const isProcessing = procState === 'reading' || procState === 'processing' || procState === 'zipping';

  const stateLabel: Record<ProcessingState, string> = {
    idle: '',
    reading: 'Lecture du fichier...',
    processing: 'Conversion en cours...',
    zipping: 'Création du ZIP...',
    done: 'Terminé',
    error: 'Erreur',
  };

  return (
    <div>
      <div className="mb-6">
        <button onClick={() => navigate('/')} className="text-ms-blue text-sm hover:underline mb-2 inline-block">
          ← Retour au portail
        </button>
        <h2 className="text-2xl font-semibold text-ms-gray-800">Export images Odoo</h2>
        <p className="text-ms-gray-500 mt-1">
          Convertit les images{' '}
          <code className="text-xs bg-ms-gray-100 px-1 py-0.5 rounded">image_1920</code>{' '}
          base64 d'un export CSV Odoo en fichiers image téléchargeables (ZIP).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-6">

          {/* Zone de dépôt */}
          <div className="bg-white border border-ms-gray-200 rounded-lg p-6">
            <h3 className="text-base font-semibold text-ms-gray-800 mb-4">Fichier CSV Odoo</h3>
            <label
              onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              onDragOver={e => e.preventDefault()}
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-12 transition-colors
                ${isProcessing
                  ? 'border-ms-gray-200 bg-ms-gray-50 cursor-not-allowed'
                  : 'border-ms-blue/30 hover:border-ms-blue hover:bg-ms-blue/5 cursor-pointer'
                }`}
            >
              <svg className="w-10 h-10 text-ms-blue/40 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm text-ms-gray-600 font-medium">Glisser-déposer ou cliquer pour sélectionner</p>
              <p className="text-xs text-ms-gray-400 mt-1">Fichier .csv brut exporté depuis Odoo</p>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                disabled={isProcessing}
                onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
              />
            </label>
          </div>

          {/* Progression + résultats */}
          {procState !== 'idle' && (
            <div className="bg-white border border-ms-gray-200 rounded-lg p-6">
              <h3 className="text-base font-semibold text-ms-gray-800 mb-4">{stateLabel[procState]}</h3>

              {procState !== 'error' && (
                <>
                  <div className="w-full bg-ms-gray-100 rounded-full h-2 mb-1">
                    <div
                      className="bg-ms-blue h-2 rounded-full transition-all duration-200"
                      style={{ width: `${stats.progress}%` }}
                    />
                  </div>
                  {stats.currentName && (
                    <p className="text-xs text-ms-gray-400 mb-4 truncate">
                      Traitement : {stats.currentName}
                    </p>
                  )}

                  <div className="grid grid-cols-3 gap-4 text-center mt-4">
                    <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                      <p className="text-3xl font-bold text-green-700">{stats.ok}</p>
                      <p className="text-xs text-ms-gray-500 mt-1">Images converties</p>
                    </div>
                    <div className="bg-ms-gray-50 border border-ms-gray-100 rounded-lg p-4">
                      <p className="text-3xl font-bold text-ms-gray-400">{stats.skipped}</p>
                      <p className="text-xs text-ms-gray-500 mt-1">Sans image</p>
                    </div>
                    <div className={`border rounded-lg p-4 ${stats.errors > 0 ? 'bg-red-50 border-red-100' : 'bg-ms-gray-50 border-ms-gray-100'}`}>
                      <p className={`text-3xl font-bold ${stats.errors > 0 ? 'text-red-600' : 'text-ms-gray-400'}`}>{stats.errors}</p>
                      <p className="text-xs text-ms-gray-500 mt-1">Erreurs</p>
                    </div>
                  </div>
                </>
              )}

              {procState === 'error' && errorMsg && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                  <p className="font-semibold mb-1">Conversion interrompue</p>
                  <p>{errorMsg}</p>
                </div>
              )}

              {procState === 'done' && downloadUrl && (
                <>
                  <a
                    href={downloadUrl}
                    download={downloadName}
                    className="mt-5 flex items-center justify-center gap-2 w-full px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-semibold"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger le ZIP ({stats.ok} image{stats.ok > 1 ? 's' : ''})
                  </a>
                  {stats.excelWarning && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                      <strong>Attention :</strong> toutes les images semblent petites (&lt;32 Ko). Si elles paraissent corrompues, le fichier CSV a peut-être été ouvert dans Excel avant l'upload — réexportez-le directement depuis Odoo.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Colonne droite */}
        <div className="space-y-4">
          <div className="bg-white border border-ms-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-ms-gray-800 mb-3">Comment exporter depuis Odoo</h3>
            <ol className="space-y-3">
              {[
                <>Dans la page <strong>Produits</strong>, filtrer les produits souhaités puis passer en <strong>vue liste</strong></>,
                <>Cliquer sur <strong>Action → Exporter</strong></>,
                <>Sélectionner le format <strong>CSV</strong></>,
                <>Rechercher et sélectionner le modèle <strong>"Export images Odoo"</strong></>,
                <>Cliquer sur <strong>Exporter</strong> et uploader le fichier ici <strong>sans l'ouvrir dans Excel</strong></>,
              ].map((step, i) => (
                <li key={i} className="flex gap-2 text-sm text-ms-gray-600">
                  <span className="flex-shrink-0 w-5 h-5 bg-ms-blue/10 rounded-full flex items-center justify-center text-ms-blue text-xs font-bold">
                    {i + 1}
                  </span>
                  <p>{step}</p>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-amber-800">Ne pas ouvrir le fichier avec Excel</p>
                <p className="text-xs text-amber-700 mt-1">
                  Téléchargez le fichier CSV depuis Odoo et uploadez-le directement ici.
                  Excel corromprait les images en tronquant les données.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-ms-gray-200 rounded-lg p-4">
            <div className="flex gap-2">
              <svg className="w-5 h-5 text-ms-blue flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-ms-gray-700">Traitement 100% local</p>
                <p className="text-xs text-ms-gray-500 mt-1">
                  Le fichier CSV reste sur votre ordinateur — il n'est jamais transmis à un serveur. La conversion s'effectue directement dans votre navigateur.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OdooImages;
